import { useState } from 'react';
import { FileText, Copy, Check, Loader2, Info } from 'lucide-react';
import { Button, Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@design-system';
import { getPolicyTypeFromProductName } from '@shared/calculations/roi/calcClientROIProjections';
import { isCareMattersAnnuity } from '@shared/FundingPolicyTypeDef';
import { MonitorQuote, IllustrationMonitorStatus } from '@/features/illustration-monitor/types';

/**
 * `InsuranceQuotes.policyType` is a SHOPPING CATEGORY, not a description of the
 * product's chassis. Its four values — 'Single Life', 'Joint Life', 'Standard',
 * 'Couples Discount' — are already inconsistent across life products (Asset Flex
 * and SecureCare load as Standard/Couples Discount; Asset Care and CareMatters
 * Together as Single/Joint Life). It is load-bearing: the joint quote query, the
 * married client's allowedPolicyTypes, and the precompute partner match all key
 * off it, so the stored value must stay exactly as it is.
 *
 * Rendering it raw is what put "Policy: Joint Life" on CareMatters Annuity — an
 * ANNUITY, with no life chassis and no face amount, whose death benefit is only
 * whatever contract value long-term care did not consume. Same class of mistake
 * as formatRiskClass printing "Standard Tobacco" for a Preferred annuity.
 *
 * So: correct data, wrong label. Relabel here and leave the column alone.
 */
function formatPolicyCategory(quote: MonitorQuote): string | null {
  if (!quote.policyType) return null;

  const isJoint = quote.policyType === 'Joint Life';
  const policyType = quote.productName
    ? getPolicyTypeFromProductName(quote.productName, isJoint)
    : undefined;

  if (isCareMattersAnnuity(policyType)) {
    return isJoint ? 'Joint · 2 annuitants' : 'Single annuitant';
  }

  return quote.policyType;
}

interface QuoteCardProps {
  quote: MonitorQuote;
  onGenerate: (quoteId: string, quoteType: string) => void;
  isGenerating: boolean;
  status: IllustrationMonitorStatus;
}

export function QuoteCard({ quote, onGenerate, isGenerating, status }: QuoteCardProps) {
  const [showDetails, setShowDetails] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(quote.quoteId);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const isTraditional = quote.quoteType === 'traditionalLTC';

  return (
    <div className="border-b border-gray-200 py-4">
      <div className="flex items-center justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <span className="text-base font-semibold text-gray-900">
              {fmtMoney(quote.premiumAmount)}
            </span>
            <span className="text-xs text-gray-500">
              {isTraditional ? quote.premiumFrequency ?? 'premium' : 'premium'}
            </span>
          </div>
          <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-gray-600">
            {isTraditional ? (
              <>
                <Stat label="Benefit pool" value={fmtMoney(quote.benefitPool)} />
                <Stat label="Daily benefit" value={fmtMoney(quote.dailyBenefitAmount)} />
                <Stat label="Elim. period" value={quote.eliminationPeriod != null ? `${quote.eliminationPeriod} days` : null} />
                <Stat label="BIO" value={quote.bioPercent} />
                <Stat label="Benefit type" value={quote.primaryBenefitType} />
                <Stat label="Marital" value={quote.maritalClass} />
                <Stat label="UW class" value={quote.underwritingClass} />
              </>
            ) : (
              <>
                <Stat label="Death benefit" value={fmtMoney(quote.deathBenefitInitial)} />
                <Stat label="Monthly LTC" value={fmtMoney(quote.ltcBenefitMonthlyIndividual)} />
                <Stat label="Total LTC" value={fmtMoney(quote.ltcBenefitTotal)} />
                <Stat label="Duration" value={quote.ltcBenefitDurationMonths ? `${quote.ltcBenefitDurationMonths}mo` : null} />
                <Stat label="Policy" value={formatPolicyCategory(quote)} />
                <Stat label="AOB" value={quote.aobDuration} />
                <Stat label="COB" value={quote.cobDuration} />
                <Stat label="COB payment" value={quote.cobPaymentOption} />
              </>
            )}
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-1">
          <StatusBadge status={status} />

          <TooltipProvider delayDuration={100}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => onGenerate(quote.quoteId, quote.quoteType)}
                  disabled={isGenerating || isInFlight(status)}
                >
                  {isGenerating || isInFlight(status) ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <FileText className="h-4 w-4" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent sideOffset={4}>Generate illustration</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setShowDetails(d => !d)}
                >
                  <Info className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent sideOffset={4}>Full product details</TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={handleCopy}
                >
                  {copied ? (
                    <Check className="h-4 w-4 text-green-600" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent sideOffset={4}>{copied ? 'Copied!' : 'Copy quote ID'}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>

      {showDetails && (
        <div className="mt-3 rounded-md bg-gray-50 px-4 py-3">
          <dl className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-xs sm:grid-cols-3">
            <Detail label="Quote ID" value={quote.quoteId} mono />
            <Detail label="Type" value={quote.quoteType} />
            <Detail label="Rated state" value={quote.ratedState} />
            <Detail label="Policy type" value={formatPolicyCategory(quote)} />
            <Detail label="Primary" value={formatPerson(quote.primaryAge, quote.primaryGender, quote.primaryTobaccoStatus)} />
            <Detail label="Secondary" value={formatPerson(quote.secondaryAge, quote.secondaryGender, quote.secondaryTobaccoStatus)} />
            <Detail label="Premium" value={fmtMoney(quote.premiumAmount)} />
            <Detail label="Death benefit" value={fmtMoney(quote.deathBenefitInitial)} />
            <Detail label="Monthly LTC" value={fmtMoney(quote.ltcBenefitMonthlyIndividual)} />
            <Detail label="Total LTC" value={fmtMoney(quote.ltcBenefitTotal)} />
            <Detail label="LTC duration" value={quote.ltcBenefitDurationMonths ? `${quote.ltcBenefitDurationMonths} months` : null} />
            <Detail label="AOB duration" value={quote.aobDuration} />
            <Detail label="COB duration" value={quote.cobDuration} />
            <Detail label="AOB inflation" value={quote.aobInflation} />
            <Detail label="COB inflation" value={quote.cobInflation} />
            <Detail label="COB payment" value={quote.cobPaymentOption} />
            <Detail label="Benefit pool" value={fmtMoney(quote.benefitPool)} />
            <Detail label="Daily benefit" value={fmtMoney(quote.dailyBenefitAmount)} />
            <Detail label="Elim. period" value={quote.eliminationPeriod != null ? `${quote.eliminationPeriod} days` : null} />
            <Detail label="BIO" value={quote.bioPercent} />
            <Detail label="Premium freq." value={quote.premiumFrequency} />
            <Detail label="Marital class" value={quote.maritalClass} />
            <Detail label="UW class" value={quote.underwritingClass} />
            <Detail label="Benefit type" value={quote.primaryBenefitType} />
          </dl>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string | number | null }) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <span>
      <span className="text-gray-500">{label}</span>{' '}
      <span className="text-gray-800">{value}</span>
    </span>
  );
}

function Detail({ label, value, mono }: { label: string; value: string | number | null; mono?: boolean }) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div className="flex justify-between gap-2">
      <dt className="text-gray-500">{label}</dt>
      <dd className={`text-gray-900 ${mono ? 'font-mono text-[10px]' : ''}`}>{value}</dd>
    </div>
  );
}

function StatusBadge({ status }: { status: IllustrationMonitorStatus }) {
  if (!status) return null;
  if (status.status === 'completed') {
    return status.pdfUrl ? (
      <a href={status.pdfUrl} target="_blank" rel="noreferrer" className="mr-1 text-xs font-medium text-primary underline">
        View PDF
      </a>
    ) : (
      <span className="mr-1 text-xs font-medium text-green-700">Done</span>
    );
  }
  if (status.status === 'error') {
    return <span className="mr-1 max-w-[10rem] truncate text-xs text-red-600" title={status.error ?? ''}>{status.error ?? 'Error'}</span>;
  }
  return <span className="mr-1 text-xs text-gray-400">{status.status}...</span>;
}

function isInFlight(status: IllustrationMonitorStatus) {
  if (!status) return false;
  return status.status !== 'completed' && status.status !== 'error';
}

function formatPerson(age: number | null, gender: string | null, tobacco: string | null) {
  if (!age && !gender) return null;
  return [age, gender, tobacco].filter(Boolean).join(' · ');
}

function fmtMoney(n: number | null): string | null {
  if (n === null || n === undefined) return null;
  return `$${Math.round(n).toLocaleString()}`;
}
